import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader2, ShieldX, LogOut } from 'lucide-react';
import { authApi } from '../services/api';
import { getBaseOriginUrl } from '../utils/basePath';
import { clearSageSession } from '../utils/sageSession';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { user, isAuthenticated, logout } = useAuth0();
  const [profile, setProfile] = useState(null);
  const [navigation, setNavigation] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(null); // null = not checked, string = denial message

  const loadUserData = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      // Sync user to DB — will return 403 if not provisioned
      const syncRes = await authApi.syncUser({
        email: user.email,
        firstName: user.given_name || '',
        lastName: user.family_name || '',
        pictureUrl: user.picture || '',
      });

      if (!syncRes.data?.authorized) {
        setAccessDenied('Your account has not been provisioned for this application. Please contact your administrator.');
        setLoading(false);
        return;
      }

      // Load profile and navigation in parallel
      const [profileRes, navRes] = await Promise.all([
        authApi.getProfile(),
        authApi.getNavigation(),
      ]);

      setProfile(profileRes.data);
      setNavigation(navRes.data);
      setPermissions(profileRes.data?.permissions || []);
      setAccessDenied(null);
    } catch (err) {
      console.error('Failed to load user data:', err.message);
      // Any failure during sync/profile load blocks access
      // This covers: 403 (not provisioned), DB errors (tables missing), network errors
      const msg = err.response?.data?.message || err.message || 'Unable to verify your access. Please contact your administrator.';
      setAccessDenied(msg);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user, loadUserData]);

  // '*' is the wildcard grant — the local-dev mock and any all-access role use
  // it to mean "every permission". Honor it here so a wildcard holder passes
  // every check, rather than failing a literal key match against ['*'].
  const hasPermission = useCallback(
    (permissionKey) => permissions.includes('*') || permissions.includes(permissionKey),
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (...keys) => permissions.includes('*') || keys.some((k) => permissions.includes(k)),
    [permissions]
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img src="/favicon.svg" alt="Sunny Benefits" className="w-16 h-16" />
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          <p className="text-gray-500 text-sm">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Access denied state
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full mx-4 text-center">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldX className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-sm text-gray-500 mb-2">{accessDenied}</p>
          <p className="text-xs text-gray-400 mb-6">
            Signed in as <span className="font-medium text-gray-600">{user?.email}</span>
          </p>
          <button
            onClick={() => {
              clearSageSession();
              logout({ logoutParams: { returnTo: getBaseOriginUrl() } });
            }}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        profile,
        navigation,
        permissions,
        loading,
        hasPermission,
        hasAnyPermission,
        reload: loadUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAppAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Local dev mode — no AuthProvider wrapping the app
    return {
      profile: { roleNames: ['dev'] },
      navigation: [],
      permissions: [],
      loading: false,
      hasPermission: () => true,
      hasAnyPermission: () => true,
      reload: () => {},
    };
  }
  return ctx;
}
